import { IOrganisationConfig } from 'fitbeat.models';
import React from 'react';
import Modal from 'react-modal';
import '../../../styles/members/membersAnalyticsOverlay.scss';
import BreakdownMetricCard from './BreakdownMetricCard';
import { formatNumber, formatStatsDateRange } from './format';
import { CategoryAnalyticsIcon } from './membersAnalyticsIcon';
import { printMembersAnalyticsDocument } from './membersAnalyticsPdf';
import {
  getMembersAnalyticsBranding,
  MEMBERS_ANALYTICS_POWERED_BY_SRC,
} from './membersAnalyticsBranding';
import {
  AnalyticsMemberRow,
  buildMembersAnalytics,
  getEngagedUsersFootnote,
  MembersAnalytics,
  MembersAnalyticsEngagementBasis,
  parseOptionalCountInput,
  parseTotalMembersInput,
} from './membersAnalyticsLogic';
import RingMetricCard from './RingMetricCard';
import SummaryMetricCard from './SummaryMetricCard';

interface IProps {
  rows: AnalyticsMemberRow[];
  isOpen: boolean;
  gymDisplayName: string;
  organisationConfig?: IOrganisationConfig;
  statsDateRange?: { startDate?: Date, endDate?: Date };
  statsTimezone?: string;
  onClose: () => void;
}

interface IState {
  totalMembersInput: string;
  studioMembersInput: string;
  studioWorkoutsInput: string;
  engagementBasis: MembersAnalyticsEngagementBasis;
}

const initialOverlayState = (): IState => ({
  totalMembersInput: '',
  studioMembersInput: '',
  studioWorkoutsInput: '',
  engagementBasis: 'joinedTeam',
});

function getRowsFingerprint(rows: AnalyticsMemberRow[]): string {
  let registeredCount = 0;
  for (const row of rows) {
    if (row.email != null && String(row.email).trim() !== '') {
      registeredCount += 1;
    }
  }
  return `${rows.length}:${registeredCount}`;
}

export default class MembersAnalyticsOverlay extends React.Component<IProps, IState> {
  public state: IState = initialOverlayState();

  public componentDidUpdate(prevProps: IProps): void {
    if (!prevProps.isOpen && this.props.isOpen) {
      this.setState(initialOverlayState());
      return;
    }

    if (!this.props.isOpen) {
      return;
    }

    const prevFingerprint = getRowsFingerprint(prevProps.rows);
    const nextFingerprint = getRowsFingerprint(this.props.rows);
    if (prevFingerprint !== nextFingerprint) {
      this.setState(initialOverlayState());
    }
  }

  public render() {
    const totalMembers = parseTotalMembersInput(this.state.totalMembersInput);
    const studioMembersOverride = parseOptionalCountInput(this.state.studioMembersInput);
    const studioWorkoutsOverride = parseOptionalCountInput(this.state.studioWorkoutsInput);
    const analytics = buildMembersAnalytics(this.props.rows, {
      totalMembers,
      engagementBasis: this.state.engagementBasis,
      studioMembersOverride,
      studioWorkoutsOverride,
    });
    const hasInteractions = analytics.categories.some((category) => category.interactionTotal > 0);
    const dateRange = formatStatsDateRange(this.props.statsDateRange, this.props.statsTimezone);
    const branding = getMembersAnalyticsBranding(this.props.organisationConfig);
    const reportStyle = {
      '--members-analytics-orange': branding.primaryColor,
    } as React.CSSProperties;
    const exerciseCategory = analytics.categories.find((category) => category.id === 'exercise');
    const reportCategories = analytics.categories.filter((category) => category.id !== 'exercise');

    return (
      <Modal
        isOpen={this.props.isOpen}
        onRequestClose={this.props.onClose}
        className='modal-content members-analytics-modal'
        overlayClassName='modal-overlay members-analytics-overlay'
        ariaHideApp={false}
        contentLabel='Members Analytics'>
        <div
          className='members-analytics-report'
          data-testid='members-analytics-report'
          style={reportStyle}>
          <div className='members-analytics-header'>
            <div className='members-analytics-header-main'>
              <div>
                <h2>Members Analytics</h2>
                <p>{formatNumber(analytics.registeredCount)} filtered registered members</p>
              </div>
              <label className='members-analytics-optional-input'>
                Total members (optional)
                <input
                  type='number'
                  min={1}
                  step={1}
                  value={this.state.totalMembersInput}
                  onChange={this.handleTotalMembersChange}
                />
              </label>
              <label className='members-analytics-optional-input'>
                Studio members (optional)
                <input
                  type='number'
                  min={0}
                  step={1}
                  value={this.state.studioMembersInput}
                  onChange={this.handleStudioMembersChange}
                />
              </label>
              <label className='members-analytics-optional-input'>
                Studio workouts (optional)
                <input
                  type='number'
                  min={0}
                  step={1}
                  value={this.state.studioWorkoutsInput}
                  onChange={this.handleStudioWorkoutsChange}
                />
              </label>
              <div className='members-analytics-engagement-basis'>
                <span className='members-analytics-engagement-basis-label'>Engaged users basis</span>
                <div className='members-analytics-engagement-basis-toggle'>
                  <button
                    type='button'
                    className={this.state.engagementBasis === 'onboarded'
                      ? 'members-analytics-engagement-basis-option is-active'
                      : 'members-analytics-engagement-basis-option'}
                    onClick={() => this.setEngagementBasis('onboarded')}>
                    Onboarded
                  </button>
                  <button
                    type='button'
                    className={this.state.engagementBasis === 'joinedTeam'
                      ? 'members-analytics-engagement-basis-option is-active'
                      : 'members-analytics-engagement-basis-option'}
                    onClick={() => this.setEngagementBasis('joinedTeam')}>
                    Joined team
                  </button>
                </div>
              </div>
            </div>
            <button
              type='button'
              className='members-analytics-export-pdf'
              data-testid='members-analytics-export-pdf'
              onClick={this.handleExportPdf}>
              Export PDF
            </button>
            <button
              className='modal-close-button members-analytics-close'
              onClick={this.props.onClose}>
              X
            </button>
          </div>

          {!hasInteractions && (
            <div className='members-analytics-warning'>
              <strong>No interaction stats found.</strong>
              <ul>
                <li>On the Members tab, find Stats.</li>
                <li>Select a date range.</li>
                <li>Press the play button to calculate stats before opening Analytics.</li>
              </ul>
            </div>
          )}

          <div className='members-analytics-report-header'>
            <div className='members-analytics-report-header-brand'>
              {branding.logoUrl && (
                <>
                  <img
                    className='members-analytics-org-logo'
                    src={branding.logoUrl}
                    alt=''
                  />
                  <div className='members-analytics-report-header-divider' aria-hidden />
                </>
              )}
              <div className='members-analytics-report-header-title'>
                <div className='members-analytics-organisation'>
                  {this.props.gymDisplayName} Engagement Report
                </div>
                <div className='members-analytics-report-header-divider' aria-hidden />
                <div className='members-analytics-report-date'>{dateRange}</div>
              </div>
            </div>
            <div className='members-analytics-powered-by'>
              <img
                className='members-analytics-powered-by-logo'
                src={MEMBERS_ANALYTICS_POWERED_BY_SRC}
                alt='Powered by Fitillion'
              />
            </div>
          </div>

          <div className='members-analytics-document'>
            <div className='members-analytics-report-main'>
              <div className='members-analytics-report-grid'>
                <div className='members-analytics-activation-panel'>
                  <h3>Activation Funnel</h3>
                  {analytics.funnel.map((item) => (
                    <SummaryMetricCard
                      item={item}
                      key={item.label}
                    />
                  ))}
                </div>
                {exerciseCategory && this.renderCategoryColumn(exerciseCategory, analytics, true)}
                <div className='members-analytics-category-stack'>
                  <div className='members-analytics-category-stack-columns'>
                    {reportCategories.map((category) => (
                      this.renderCategoryColumn(category, analytics)
                    ))}
                  </div>
                  <div className='members-analytics-report-footer'>
                    <p>
                      <span className='members-analytics-footnote-marker'>*</span>
                      {' '}
                      {getEngagedUsersFootnote(this.state.engagementBasis)}
                    </p>
                    <p>
                      <span className='members-analytics-footnote-marker'>**</span>
                      {' '}
                      Interactions: total engagement activities across all members during the period.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    );
  }

  private renderCategoryColumn(
    category: MembersAnalytics['categories'][number],
    analytics: MembersAnalytics,
    standalone = false,
  ) {
    const className = standalone
      ? 'members-analytics-category-column members-analytics-category-column--standalone'
      : 'members-analytics-category-column';

    return (
      <div className={className} key={category.id}>
        <div className='members-analytics-category-header'>
          <div className='members-analytics-category-icon-placeholder'>
            <CategoryAnalyticsIcon
              categoryId={category.id}
              className='members-analytics-category-icon'
            />
          </div>
          <h4>{category.label}</h4>
        </div>
        <div className='members-analytics-category-body'>
          <div className='members-analytics-engaged'>
            <div className='members-analytics-engaged-label'>
              Engaged users:
              <span className='members-analytics-footnote-marker'>*</span>
            </div>
            <span className='members-analytics-engaged-tooltip' role='tooltip'>
              {category.engagedUsersTooltip}
            </span>
            <RingMetricCard category={category} />
          </div>
          <BreakdownMetricCard
            category={category}
            stepsTotal={analytics.stepsTotal}
            studioWorkoutsTotal={analytics.studioWorkoutsTotal}
          />
        </div>
      </div>
    );
  }

  private handleTotalMembersChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ totalMembersInput: event.target.value });
  };

  private handleStudioMembersChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ studioMembersInput: event.target.value });
  };

  private handleStudioWorkoutsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ studioWorkoutsInput: event.target.value });
  };

  private setEngagementBasis = (engagementBasis: MembersAnalyticsEngagementBasis) => {
    this.setState({ engagementBasis });
  };

  private handleExportPdf = () => {
    const dateRange = formatStatsDateRange(this.props.statsDateRange, this.props.statsTimezone);
    printMembersAnalyticsDocument(this.props.gymDisplayName, dateRange);
  };
}
